import { createSelector } from 'reselect';
import { add } from 'utils/generic/calculator';
import { getSelectorRoot } from 'utils/selectors';
import { EAdvancedSelectedTabs } from 'interfaces/bet-data';
import { EBetslipBetType, EBetslipType } from 'interfaces/betslip';
import { GENERAL_FIELD_KEY } from 'newelements/_Betslip/utils/constants/shared';
import { getAllBets } from 'store/selectors/betslip/bets';
import {
  getAdvancedSelectedTab,
  getBetslipType,
  getComputedBetType,
  getType
} from 'store/selectors/betslip/configs';
import { getTotalBetStake } from 'store/selectors/betslip/stake';

const getBetSlip = getSelectorRoot('betSlip');

const getBetsCalculations = getBetSlip('calculations');

const getSingleEventCalculationData = (eventId?: number | string) =>
  createSelector(
    [getBetsCalculations, getComputedBetType],
    ({ singleEventsCalculations, general }, betType) => {
      if (!eventId) {
        return null;
      }

      if (eventId === GENERAL_FIELD_KEY) {
        if (betType === EBetslipBetType.SINGLE) {
          let generalStakeForSingleEvents = Object.values(
            singleEventsCalculations
          )?.[0]?.stake;

          Object.values(singleEventsCalculations).forEach(calculation => {
            if (
              !generalStakeForSingleEvents ||
              calculation?.stake !== generalStakeForSingleEvents
            ) {
              generalStakeForSingleEvents = '';
            }
          });

          return {
            ...general,
            stake: generalStakeForSingleEvents
          };
        }

        return general;
      }

      return singleEventsCalculations[eventId];
    }
  );

const getIsCounterOfferActiveByEventId = (eventId: string | number) =>
  createSelector([getBetsCalculations], ({ singleEventsCalculations }) => {
    return !!(
      singleEventsCalculations[eventId] &&
      'oddOffer' in singleEventsCalculations[eventId]
    );
  });

const getIsSuperBetActiveByEventId = (eventId: string | number) =>
  createSelector(
    [getBetsCalculations],
    ({ general, singleEventsCalculations }) => {
      if (eventId === GENERAL_FIELD_KEY) {
        return !!general?.superBet;
      }

      return !!singleEventsCalculations[eventId]?.superBet;
    }
  );

const getCounterOfferActiveEventIds = createSelector(
  [getBetsCalculations],
  ({ singleEventsCalculations }) => {
    return Object.entries(singleEventsCalculations).reduce<(string | number)[]>(
      (ids, [eventId, calc]) => {
        return calc && 'oddOffer' in calc ? [...ids, eventId] : ids;
      },
      []
    );
  }
);

const getCounterOffersFromCalculations = createSelector(
  [getBetsCalculations, getCounterOfferActiveEventIds],
  ({ singleEventsCalculations }, ids) => {
    return ids.reduce((acc, id) => {
      return {
        ...acc,
        [id]: singleEventsCalculations[id]?.oddOffer
      };
    }, {} as Record<number, string>);
  }
);

const getSuperBetActiveEventIds = createSelector(
  [getBetsCalculations],
  ({ singleEventsCalculations, general }) => {
    const eventIds = Object.entries(singleEventsCalculations).reduce<
      (string | number)[]
    >((ids, [eventId, calc]) => {
      return calc?.superBet ? [...ids, eventId] : ids;
    }, []);

    if (general?.superBet) {
      eventIds.push(GENERAL_FIELD_KEY);
    }

    return eventIds;
  }
);

const getSingleEventsStakes = createSelector(
  [getBetsCalculations],
  ({ singleEventsCalculations }) => {
    return Object.values(singleEventsCalculations).reduce(
      (acc, calc) => acc + +(calc?.stake ?? 0),
      0
    );
  }
);

const getAllSingleEventsPossinleWin = createSelector(
  [getBetsCalculations],
  ({ singleEventsCalculations }) => {
    return Object.values(singleEventsCalculations).reduce(
      (totalReturn, calculation) => {
        return totalReturn + calculation?.finalResult?.possibleWin || 0;
      },
      0
    );
  }
);

const _getAdvancedStake = (eventId?: string | number, isFinal = false) =>
  createSelector(
    [getAllBets, getBetsCalculations, getTotalBetStake, getAdvancedSelectedTab],
    (allBets, { singleEventsCalculations }, totalBetStake, activeTab) => {
      if (allBets.length === 1 || (eventId && eventId !== GENERAL_FIELD_KEY)) {
        return (
          (isFinal
            ? singleEventsCalculations[eventId ?? allBets[0].eventId]
                ?.finalStake
            : singleEventsCalculations[eventId ?? allBets[0].eventId]
                ?.finalResult?.stake) ?? 0
        );
      }

      if (activeTab === EAdvancedSelectedTabs.QUICKBET) {
        return totalBetStake;
      }

      const singleStakes = Object.values(singleEventsCalculations).reduce(
        (acc, calculation) => {
          return add(
            acc,
            (isFinal
              ? calculation?.finalStake
              : calculation?.finalResult?.stake) ?? 0
          );
        },
        0
      );

      return add(singleStakes, totalBetStake);
    }
  );

const getAdvancedFinalStakeAmount = (eventId?: string | number) =>
  _getAdvancedStake(eventId, true);

const getAdvancedTotalStake = (eventId?: string | number) =>
  _getAdvancedStake(eventId, false);

const getAllStakes = createSelector(
  [getBetsCalculations],
  ({ singleEventsCalculations, general }) => {
    return {
      general: general?.stake,
      single: Object.entries(singleEventsCalculations).reduce<
        Record<string | number, string | undefined>
      >((acc, [id, info]) => ({ ...acc, [id]: info?.stake ?? '' }), {})
    };
  }
);

const getBetsTotalStake = createSelector(
  [
    getType,
    getBetslipType,
    getAdvancedFinalStakeAmount(),
    getBetsCalculations,
    getAllStakes
  ],
  (
    betType,
    betslipType,
    advancedTotalStake,
    singleEventsCalculations,
    allStakes
  ) => {
    if (betslipType === EBetslipType.BASIC) {
      switch (betType) {
        case EBetslipBetType.SINGLE:
          return Object.values(allStakes.single)?.reduce(
            (total, stake) => total + +(stake || 0),
            0
          );
        case EBetslipBetType.SYSTEM:
          return singleEventsCalculations.general?.finalResult.stake ?? 0;
        default:
          return allStakes.general || 0;
      }
    } else {
      return advancedTotalStake || 0;
    }
  }
);

export {
  getAllStakes,
  getSingleEventsStakes,
  getBetsTotalStake,
  getBetsCalculations,
  getAdvancedTotalStake,
  getAdvancedFinalStakeAmount,
  getAllSingleEventsPossinleWin,
  getIsSuperBetActiveByEventId,
  getCounterOfferActiveEventIds,
  getSuperBetActiveEventIds,
  getIsCounterOfferActiveByEventId,
  getCounterOffersFromCalculations,
  getSingleEventCalculationData
};
